/**
 * Quote Cart Module
 * Custom localStorage-based quote request system
 * Replaces Shopify cart for quote functionality
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    STORAGE_KEYS: {
      ITEMS: 'quoteCartItems',
      FORM_DATA: 'quoteCartFormData',
      METADATA: 'quoteCartMetadata',
      SUBMITTED: 'quoteRequestSubmitted'
    },
    EXPIRY_HOURS: 720, // 30 days - effectively disabled
    CHECK_INTERVAL: 60000, // 1 minute
    EVENTS: {
      UPDATED: 'quoteCartUpdated',
      CLEARED: 'quoteCartCleared',
      ITEM_ADDED: 'quoteItemAdded',
      ITEM_REMOVED: 'quoteItemRemoved'
    }
  };

  // Utility functions
  const Utils = {
    // Generate unique ID for quote items
    generateId: function() {
      return 'quote_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    },

    // Check if localStorage is available
    isLocalStorageAvailable: function() {
      try {
        const test = '__localStorage_test__';
        localStorage.setItem(test, test);
        localStorage.removeItem(test);
        return true;
      } catch (e) {
        return false;
      }
    },

    // Safe JSON parse
    parseJSON: function(str, fallback = null) {
      try {
        return JSON.parse(str);
      } catch (e) {
        console.error('QuoteCart: JSON parse error', e);
        return fallback;
      }
    },

    // Format money (basic implementation)
    formatMoney: function(cents) {
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(cents);
      }
      return '$' + (cents / 100).toFixed(2);
    },

    // Dispatch custom event
    dispatchEvent: function(eventName, detail = {}) {
      window.dispatchEvent(new CustomEvent(eventName, { detail: detail }));
    }
  };

  // Storage Manager
  const Storage = {
    get: function(key) {
      if (!Utils.isLocalStorageAvailable()) return null;
      const data = localStorage.getItem(key);
      return data ? Utils.parseJSON(data) : null;
    },

    set: function(key, value) {
      if (!Utils.isLocalStorageAvailable()) return false;
      try {
        localStorage.setItem(key, JSON.stringify(value));
        return true;
      } catch (e) {
        console.error('QuoteCart: Storage error', e);
        return false;
      }
    },

    remove: function(key) {
      if (!Utils.isLocalStorageAvailable()) return false;
      try {
        localStorage.removeItem(key);
        return true;
      } catch (e) {
        console.error('QuoteCart: Remove error', e);
        return false;
      }
    },

    clear: function() {
      Object.values(CONFIG.STORAGE_KEYS).forEach(key => {
        Storage.remove(key);
      });
    }
  };

  // Main QuoteCart object
  window.QuoteCart = {
    /**
     * Initialize the quote cart system
     */
    init: function(options = {}) {
      // Override default expiry hours if provided
      if (options.expiryHours) {
        CONFIG.EXPIRY_HOURS = parseInt(options.expiryHours);
      }

      // Check expiry on init to clear old quotes
      this.checkExpiry();

      // Check if quote was just submitted
      this.checkAndShowThankYouMessage();

      // Attach form submit handler to consolidate fields into contact[body]
      this.attachFormSubmitHandler();

      console.log('QuoteCart initialized', {
        expiryHours: CONFIG.EXPIRY_HOURS,
        itemCount: this.getItemCount()
      });
    },

    /**
     * Get all quote items
     */
    getItems: function() {
      const items = Storage.get(CONFIG.STORAGE_KEYS.ITEMS);
      return Array.isArray(items) ? items : [];
    },

    /**
     * Get total item count (sum of quantities)
     */
    getItemCount: function() {
      const items = this.getItems();
      return items.reduce((total, item) => total + (item.quantity || 0), 0);
    },

    /**
     * Get metadata
     */
    getMetadata: function() {
      return Storage.get(CONFIG.STORAGE_KEYS.METADATA) || {
        createdAt: Date.now(),
        expiresAt: Date.now() + (CONFIG.EXPIRY_HOURS * 60 * 60 * 1000),
        lastUpdated: Date.now()
      };
    },

    /**
     * Update metadata
     */
    updateMetadata: function(updates = {}) {
      const metadata = this.getMetadata();
      const updated = Object.assign({}, metadata, updates, {
        lastUpdated: Date.now()
      });
      Storage.set(CONFIG.STORAGE_KEYS.METADATA, updated);
    },

    /**
     * Add item to quote cart
     */
    addItem: function(productData) {
      const items = this.getItems();

      // Check if item already exists (same variant)
      const existingItemIndex = items.findIndex(item =>
        item.variantId === productData.variantId
      );

      if (existingItemIndex !== -1) {
        // Update quantity of existing item
        items[existingItemIndex].quantity += (productData.quantity || 1);
        items[existingItemIndex].customFields = Object.assign(
          {},
          items[existingItemIndex].customFields || {},
          productData.customFields || {}
        );
      } else {
        // Add new item
        const newItem = {
          id: Utils.generateId(),
          variantId: productData.variantId,
          productId: productData.productId,
          title: productData.title,
          variantTitle: productData.variantTitle || '',
          url: productData.url,
          image: productData.image,
          quantity: productData.quantity || 1,
          price: productData.price || 0,
          inventoryQuantity: productData.inventoryQuantity || 0,
          inventoryManagement: productData.inventoryManagement || null,
          inventoryPolicy: productData.inventoryPolicy || 'deny',
          productType: productData.productType || '',
          vendor: productData.vendor || '',
          customFields: productData.customFields || {},
          addedAt: Date.now()
        };
        items.push(newItem);
      }

      // Save items
      Storage.set(CONFIG.STORAGE_KEYS.ITEMS, items);

      // Update metadata
      if (items.length === 1) {
        // First item - set creation time
        this.updateMetadata({
          createdAt: Date.now(),
          expiresAt: Date.now() + (CONFIG.EXPIRY_HOURS * 60 * 60 * 1000)
        });
      } else {
        this.updateMetadata();
      }

      // Dispatch events
      Utils.dispatchEvent(CONFIG.EVENTS.ITEM_ADDED, { item: productData });
      Utils.dispatchEvent(CONFIG.EVENTS.UPDATED);

      console.log('QuoteCart: Item added', productData);
      return true;
    },

    /**
     * Remove item from quote cart
     */
    removeItem: function(itemId) {
      let items = this.getItems();
      const initialLength = items.length;

      items = items.filter(item => item.id !== itemId);

      if (items.length !== initialLength) {
        Storage.set(CONFIG.STORAGE_KEYS.ITEMS, items);
        this.updateMetadata();

        Utils.dispatchEvent(CONFIG.EVENTS.ITEM_REMOVED, { itemId: itemId });
        Utils.dispatchEvent(CONFIG.EVENTS.UPDATED);

        console.log('QuoteCart: Item removed', itemId);
        return true;
      }

      return false;
    },

    /**
     * Update item quantity
     */
    updateQuantity: function(itemId, quantity) {
      const items = this.getItems();
      const item = items.find(i => i.id === itemId);

      if (item) {
        const newQty = parseInt(quantity);

        // Remove item if quantity is 0
        if (newQty <= 0) {
          return this.removeItem(itemId);
        }

        // Update quantity
        item.quantity = newQty;
        Storage.set(CONFIG.STORAGE_KEYS.ITEMS, items);
        this.updateMetadata();

        Utils.dispatchEvent(CONFIG.EVENTS.UPDATED);
        console.log('QuoteCart: Quantity updated', { itemId, quantity: newQty });
        return true;
      }

      return false;
    },

    /**
     * Update item custom field (e.g., location)
     */
    updateCustomField: function(itemId, fieldName, value) {
      const items = this.getItems();
      const item = items.find(i => i.id === itemId);

      if (item) {
        if (!item.customFields) {
          item.customFields = {};
        }
        item.customFields[fieldName] = value;

        Storage.set(CONFIG.STORAGE_KEYS.ITEMS, items);
        this.updateMetadata();

        // Don't dispatch UPDATED event to avoid re-rendering while user is typing
        console.log('QuoteCart: Custom field updated', { itemId, fieldName, value });
        return true;
      }

      return false;
    },

    /**
     * Clear all items from quote cart
     */
    clear: function() {
      Storage.remove(CONFIG.STORAGE_KEYS.ITEMS);
      Storage.remove(CONFIG.STORAGE_KEYS.METADATA);

      Utils.dispatchEvent(CONFIG.EVENTS.CLEARED);
      Utils.dispatchEvent(CONFIG.EVENTS.UPDATED);

      console.log('QuoteCart: Cleared');
    },

    /**
     * Check if quote has expired and clear if needed
     */
    checkExpiry: function() {
      const metadata = this.getMetadata();
      const now = Date.now();

      console.log('QuoteCart: Checking expiry', {
        now: new Date(now),
        expiresAt: new Date(metadata.expiresAt),
        expired: now >= metadata.expiresAt
      });

      if (metadata.expiresAt && now >= metadata.expiresAt) {
        console.log('QuoteCart: Quote expired, clearing...');
        this.clear();
        this.clearFormData();
        return true;
      }

      return false;
    },

    /**
     * Get time until expiry (in milliseconds)
     */
    getTimeUntilExpiry: function() {
      const metadata = this.getMetadata();
      const now = Date.now();
      return Math.max(0, metadata.expiresAt - now);
    },

    /**
     * Save form data to localStorage
     */
    saveFormData: function(formData) {
      const data = {
        data: formData,
        timestamp: Date.now()
      };
      Storage.set(CONFIG.STORAGE_KEYS.FORM_DATA, data);
      console.log('QuoteCart: Form data saved');
    },

    /**
     * Load form data from localStorage
     */
    loadFormData: function() {
      const stored = Storage.get(CONFIG.STORAGE_KEYS.FORM_DATA);

      if (!stored) return null;

      // Check if expired
      const hoursPassed = (Date.now() - stored.timestamp) / (1000 * 60 * 60);
      if (hoursPassed >= CONFIG.EXPIRY_HOURS) {
        this.clearFormData();
        return null;
      }

      return stored.data;
    },

    /**
     * Clear form data
     */
    clearFormData: function() {
      Storage.remove(CONFIG.STORAGE_KEYS.FORM_DATA);
      console.log('QuoteCart: Form data cleared');
    },

    /**
     * Mark quote as submitted
     */
    markAsSubmitted: function() {
      Storage.set(CONFIG.STORAGE_KEYS.SUBMITTED, 'true');
      console.log('QuoteCart: Marked as submitted');
    },

    /**
     * Check if quote was submitted
     */
    wasSubmitted: function() {
      return Storage.get(CONFIG.STORAGE_KEYS.SUBMITTED) === 'true';
    },

    /**
     * Clear submitted flag
     */
    clearSubmittedFlag: function() {
      Storage.remove(CONFIG.STORAGE_KEYS.SUBMITTED);
    },

    /**
     * Check and show thank you message if quote was submitted
     */
    checkAndShowThankYouMessage: function() {
      const emptyMessage = document.getElementById('emptyCartMessage');
      if (!emptyMessage) return;

      if (this.wasSubmitted() && this.getItemCount() === 0) {
        // Quote was submitted and cart is empty - show thank you message
        emptyMessage.innerHTML = 'Thanks for sending your quote request, we will get back to you';

        // Scroll to message (accounting for sticky header)
        const contactForm = document.getElementById('contact_form');
        if (contactForm) {
          const offset = 320; // Adjust based on your header height
          const elementPosition = contactForm.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      } else {
        // Normal empty cart - reset to default message
        const defaultMessage = emptyMessage.getAttribute('data-default-message') ||
          'There are no items in your cart. <a href="/">Continue Shopping</a>';
        emptyMessage.innerHTML = defaultMessage;
      }
    },

    /**
     * Attach form submit handler to consolidate all fields into contact[body]
     */
    attachFormSubmitHandler: function() {
      const form = document.getElementById('contact_form');
      if (!form) return;

      form.addEventListener('submit', function(e) {
        const items = QuoteCart.getItems();
        if (items.length === 0) return;

        let body = '--- Quote Request Details ---\n\n';

        // Loop through only hidden product textareas (style="display:none"), excluding visible contact[body]
        const productTextareas = Array.from(form.querySelectorAll('textarea[name^="contact["]')).filter(function(textarea) {
          return textarea.style.display === 'none' || getComputedStyle(textarea).display === 'none';
        });
        productTextareas.forEach(function(textarea, index) {
          body += 'Product ' + (index + 1) + ':\n' + textarea.value.trim() + '\n\n';
        });

        // Append other custom fields
        const extraFields = ['contact[Postcode]', 'contact[Referring]'];
        extraFields.forEach(function(selector) {
          const el = form.querySelector('[name="' + selector + '"]');
          if (el && el.value) {
            body += selector.replace('contact[', '').replace(']', '') + ': ' + el.value + '\n';
          }
        });

        // Find or create hidden contact[body] field
        let bodyField = form.querySelector('[name="contact[body]"]');
        if (!bodyField) {
          bodyField = document.createElement('input');
          bodyField.type = 'hidden';
          bodyField.name = 'contact[body]';
          form.appendChild(bodyField);
        }

        bodyField.value = body;
        console.log('QuoteCart: Form body field populated', body);
      });

      console.log('QuoteCart: Form submit handler attached');
    },

    /**
     * Render quote items into a container
     */
    render: function(containerId, templateFn) {
      const container = document.getElementById(containerId);
      if (!container) {
        console.error('QuoteCart: Container not found', containerId);
        return;
      }

      const items = this.getItems();

      if (items.length === 0) {
        container.innerHTML = '';
        return;
      }

      // Use custom template function if provided, otherwise use default
      if (typeof templateFn === 'function') {
        container.innerHTML = items.map((item, index) => templateFn(item, index)).join('');
      } else {
        container.innerHTML = items.map((item, index) => this.defaultItemTemplate(item, index)).join('');
      }

      // Attach event listeners after rendering
      this.attachEventListeners(container);
    },

    /**
     * Default item template (basic HTML structure)
     */
    defaultItemTemplate: function(item, index) {
      return `
        <div class="cart--info-wrapper" data-item-id="${item.id}">
          <a class="cart_image" href="${item.url}">
            ${item.image ? `<img src="${item.image}" alt="${item.title}" style="max-width: 80px; height: auto;">` : ''}
          </a>

          <div class="cart--content-wrapper">
            <p class="cart_product_title">
              <a href="${item.url}">${item.title}</a>
              <a class="remove" href="#" data-remove-item="${item.id}">
                <span class="ss-icon icon-close"></span>
              </a>
            </p>

            ${item.variantTitle ? `<p class="meta">${item.variantTitle}</p>` : ''}

            <p class="cart_product_status">
              ${item.inventoryQuantity > 0 ?
                `${item.inventoryQuantity} In stock` :
                `${item.productType || 'Product'}`
              }
            </p>

            <div class="contact__form-location">
              <input
                type="text"
                class="location-field"
                name="contact[Product-${index + 1}-Location]"
                placeholder="Location"
                value="${item.customFields.location || ''}"
                data-item-id="${item.id}"
                data-field-name="location"
              />
            </div>

            <div class="cart__itemQuantity">
              <div class="purchase-details__quantity">
                <span class="product-minus js-change-quantity" data-func="minus" data-item-id="${item.id}">
                  <span class="icon-minus"></span>
                </span>
                <input
                  class="quantity"
                  type="number"
                  min="0"
                  name="contact[Product-${index + 1}-Quantity]"
                  value="${item.quantity}"
                  data-item-id="${item.id}"
                  ${item.inventoryQuantity > 0 ? `max="${item.inventoryQuantity}"` : ''}
                >
                <span class="product-plus js-change-quantity" data-func="plus" data-item-id="${item.id}">
                  <span class="icon-plus"></span>
                </span>
              </div>
            </div>

            <p class="price_total">
              <span class="money">
                ${item.price > 0 ? Utils.formatMoney(item.price) : 'Price with Quote'}
              </span>
            </p>

            <input type="hidden" name="contact[Product-${index + 1}]" value="${item.title}${item.variantTitle ? ' - ' + item.variantTitle : ''}">
            <textarea name="contact[${item.title}]" style="display:none;">
Title: ${item.title}
${item.variantTitle ? 'Variant: ' + item.variantTitle : ''}
URL: ${window.location.origin}${item.url}
Quantity: ${item.quantity}
${item.customFields.location ? 'Location: ' + item.customFields.location : ''}
            </textarea>
          </div>
        </div>
      `;
    },

    /**
     * Attach event listeners to rendered items
     */
    attachEventListeners: function(container) {
      const self = this;

      // Remove buttons
      container.querySelectorAll('[data-remove-item]').forEach(btn => {
        btn.addEventListener('click', function(e) {
          e.preventDefault();
          const itemId = this.dataset.removeItem;
          self.removeItem(itemId);
          self.render(container.id);
        });
      });

      // Quantity inputs
      container.querySelectorAll('.quantity').forEach(input => {
        // Save on input (as user types)
        input.addEventListener('input', function() {
          const itemId = this.dataset.itemId;
          const quantity = parseInt(this.value);
          if (!isNaN(quantity)) {
            self.updateQuantity(itemId, quantity);
          }
        });

        // Also save on change (when field loses focus) for redundancy
        input.addEventListener('change', function() {
          const itemId = this.dataset.itemId;
          const quantity = parseInt(this.value);
          if (!isNaN(quantity)) {
            self.updateQuantity(itemId, quantity);
          }
        });
      });

      // Plus/minus buttons
      container.querySelectorAll('.js-change-quantity').forEach(btn => {
        btn.addEventListener('click', function(e) {
          e.preventDefault();
          const itemId = this.dataset.itemId;
          const func = this.dataset.func;
          const input = container.querySelector(`.quantity[data-item-id="${itemId}"]`);

          if (input) {
            let currentQty = parseInt(input.value);
            const max = input.getAttribute('max');

            if (func === 'plus') {
              if (!max || currentQty < parseInt(max)) {
                currentQty++;
              }
            } else if (func === 'minus') {
              if (currentQty > 0) {
                currentQty--;
              }
            }

            input.value = currentQty;
            self.updateQuantity(itemId, currentQty);
          }
        });
      });

      // Location fields
      container.querySelectorAll('.location-field').forEach(input => {
        input.addEventListener('input', function() {
          const itemId = this.dataset.itemId;
          const fieldName = this.dataset.fieldName;
          const value = this.value;
          self.updateCustomField(itemId, fieldName, value);

          // Also update the hidden textarea immediately in the DOM
          const itemWrapper = this.closest('[data-item-id]');
          if (itemWrapper) {
            const textarea = itemWrapper.querySelector('textarea[name^="contact["]');
            if (textarea) {
              const current = textarea.value;
              // Replace or append the Location line
              if (/Location:.*/.test(current)) {
                textarea.value = current.replace(/Location:.*/, `Location: ${value}`);
              } else {
                textarea.value = current.trimEnd() + `\nLocation: ${value}`;
              }
            }
          }
        });
      });
    }
  };

  // Note: Initialization is now handled by cart-template.liquid
  // No auto-init here to prevent double initialization

})();